import { PortalError } from "@/types/portal-error";
import { Service } from "../mainService";
import { QntdRespostas } from "@/types/administracao/serigy/qntd-respostas";
import { RespostaSerigy } from "@/types/administracao/serigy/resposta-serigy";
import { RespostaDetalheSerigy } from "@/types/administracao/serigy/resposta-detalhe-serigy";
import { RespostasOutrasAvaliacoes } from "@/types/administracao/serigy/respostas-outras-avaliacoes";
import {
  Avaliacao,
  AvaliacaoJson,
} from "@/types/administracao/serigy/invoker/avaliacao";
import { RetornoRespostaAvaliacao } from "@/types/administracao/serigy/invoker/retorno-resposta-avaliacao";
import { TipoAvaliacaoGeral, TipoAvaliacaoGeralJson } from "@/types/administracao/serigy/avaliacao-geral/tipo";
import { MetricaAvaliacaoGeral, MetricaAvaliacaoGeralJson } from "@/types/administracao/serigy/avaliacao-geral/metrica";
import { AvaliacaoGeral, AvaliacaoGeralJson } from "@/types/administracao/serigy/avaliacao-geral/avaliacao-geral";
import { Ug, UgJson } from "@/types/administracao/serigy/ug-serigy/ug";

const serigyService = {
  //Rotas ligadas à tela do cassiq
  listarAvaliacoesPorUg,
  getRespostasByAvaliacaoId,
  downloadPrint,
  postResposta,
  desvalidarCriterio,

  //rotas ligadas à tela invoker
  adicionarAvaliacao,
  listarAvaliacoes,
  listarRespostasAvaliacao,

  //rotas ligadas à tela de avaliação geral
  adicionarAvaliacaoGeral,
  listarTiposAvaliacaoGeral,
  listarMetricasAvaliacaoGeral,
  listarAvaliacoesGeraisPorAno,
  listarAvaliacoesPorAvaliacaoGeral,
  listarRespostasAvaliacaoPorAvaliacaoGeral,
  reavaliarAvaliacaoGeral,
  reavaliarAvaliacao,
  togglePublicoAvaliacaoGeral,

  //rotas ligadas às telas de UG
  listarUgs,
  alterarHashUg,
  alterarUrlTansparencia,
  gerarHashUgLogada,
  getUgLogada,
};

async function listarAvaliacoesPorUg(codUgSagres: string): Promise<Avaliacao[]> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_SERIGY_JS}/cassiq/ug/${codUgSagres}/avaliacoes`
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao listar avaliações por UG:", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar avaliações por UG",
    );
  }
  return response.data.map((data: AvaliacaoJson) => new Avaliacao(data));
}

async function getRespostasByAvaliacaoId(idAvaliacao: number): Promise<{
  qtdRespostas: QntdRespostas;
  respostas: RespostaSerigy[];
  respostasOutrasAvaliacoes: RespostasOutrasAvaliacoes[];
}> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_SERIGY_JS}/cassiq/avaliacao/${idAvaliacao}/respostas`
  );

  if (response && response.status === 200) {
    return {
      qtdRespostas: new QntdRespostas(response.data.qtdRespostas),
      respostas: response.data.respostas.map((r: any) => new RespostaSerigy(r)),
      respostasOutrasAvaliacoes: response.data.respostasOutrasAvaliacoes.map(
        (r: any) => new RespostasOutrasAvaliacoes(r),
      )
    }
  }
  throw new PortalError(
    response.data?.msg ?? "Falha ao buscar dados da avaliação",
  );
}

async function downloadPrint(idResposta: number): Promise<any> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_API_SERIGY_JS}/cassiq/resposta/${idResposta}/print`,
      { responseType: "blob" },
    );
    return response.data;
  } catch (e: any) {
    let data = e.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        data = JSON.parse(text);
      } catch {
        data = { msg: text };
      }
    }
    throw new PortalError(
      data.message ??
      e.message ??
      "Falha ao fazer download da imagem",
    );
  }
}

async function postResposta(
  idAvaliacao: number,
  respostas: RespostaDetalheSerigy[],
) {
  const response = await Service.$http.post(
    `${process.env.VUE_APP_API_SERIGY_JS}/cassiq/avaliacao/${idAvaliacao}/respostas`,
    respostas,
  );
  if (response && response.status >= 200 && response.status < 400)
    return response.data;
  throw new PortalError(
    response.data?.msg ?? response.data?.message ?? "Falha ao enviar respostas",
  );
}

async function desvalidarCriterio(idAvaliacao: number, criterioId: number) {
  const response = await Service.$http.delete(
    `${process.env.VUE_APP_API_SERIGY_JS}/cassiq/avaliacao/${idAvaliacao}/criterio/${criterioId}/respostas`
  );
  if (response && response.status >= 200 && response.status < 400)
    return response.data;
  throw new PortalError(
    response.data?.msg ??
    response.data?.message ??
    "Falha ao excluir respostas",
  );
}

async function listarAvaliacoes(): Promise<Avaliacao[]> {
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_API_SERIGY_JS}/invoker
    `);
  if (!response || response.status !== 200) {
    console.error("Erro ao listar avaliações para invoker:", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar avaliações para invoker",
    );
  }
  return response.data.map((data: AvaliacaoJson) => new Avaliacao(data));
}

async function listarRespostasAvaliacao(
  avId: number,
): Promise<RetornoRespostaAvaliacao> {
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_API_SERIGY_JS}/invoker/${avId}/respostas
    `);
  if (!response || response.status !== 200) {
    console.error("Erro ao listar respostas de avaliação", avId, response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar respostas de avaliação",
    );
  }
  return new RetornoRespostaAvaliacao(response.data)
}

async function adicionarAvaliacao(url: string, metrica: number, ano: number): Promise<any> {
  const response = await Service.$http.post(
    `${process.env.VUE_APP_API_SERIGY_JS}/invoker`,
    { url, metrica, ano },
  );
  if (!response || response.status !== 201) {
    console.error('Falha ao adicionar avaliação (invoker)', url, response)
    throw new PortalError(response?.data?.msg ?? "Falha ao adicionar avaliação")
  }
  return new Avaliacao({ ...response.data, dsSituacao: 'Em Fila' });
}

async function adicionarAvaliacaoGeral(tipo: number, metrica: number, ano: number): Promise<any> {
  const response = await Service.$http.post(
    `
        ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral`,
    { tipo, metrica, ano },
  );
  if (!response || response.status !== 201) {
    console.error('Falha ao adicionar avaliação geral', { tipo, metrica, ano }, response)
    throw new PortalError(response?.data?.msg ?? "Falha ao adicionar avaliação geral")
  }
  return new AvaliacaoGeral({ ...response });
}

async function reavaliarAvaliacaoGeral(idAvaliacaoGeral: number): Promise<any> {
  const response = await Service.$http.post(
    `
          ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/${idAvaliacaoGeral}/reavaliar`
  );
  if (!response || response.status !== 201) {
    console.error('Falha ao reavaliar avaliação geral', { idAvaliacaoGeral }, response)
    throw new PortalError(response?.data?.msg ?? "Falha ao reavaliar avaliação geral")
  }
  return new AvaliacaoGeral({ ...response });
}

async function togglePublicoAvaliacaoGeral(idAvaliacaoGeral: number): Promise<any> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/${idAvaliacaoGeral}/publico`
  );
  if (!response || response.status !== 200) {
    console.error('Falha ao publicar avaliação geral', { idAvaliacaoGeral }, response)
    throw new PortalError(response?.data?.msg ?? "Falha ao reavaliar avaliação geral")
  }
  return new AvaliacaoGeral({ ...response });
}

async function reavaliarAvaliacao(idAvaliacao: number): Promise<any> {
  const response = await Service.$http.post(
    `
          ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/avaliacao/${idAvaliacao}/reavaliar`
  );
  if (!response || response.status !== 201) {
    console.error('Falha ao reavaliar avaliação', { idAvaliacao }, response)
    throw new PortalError(response?.data?.msg ?? "Falha ao reavaliar avaliação")
  }
  return { ...response };
}

async function listarTiposAvaliacaoGeral(): Promise<TipoAvaliacaoGeral[]> {
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/tipos
    `);
  if (!response || response.status !== 200) {
    console.error("Erro ao listar tipos de avaliação geral:", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar tipos de avaliação geral",
    );
  }
  return response.data.map((data: TipoAvaliacaoGeralJson) => new TipoAvaliacaoGeral(data));
}

async function listarMetricasAvaliacaoGeral(): Promise<MetricaAvaliacaoGeral[]> {
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/metricas
    `);
  if (!response || response.status !== 200) {
    console.error("Erro ao listar métricas de avaliação geral:", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar métricas de avaliação geral",
    );
  }
  return response.data.map((data: MetricaAvaliacaoGeralJson) => new MetricaAvaliacaoGeral(data));
}

async function listarAvaliacoesGeraisPorAno(ano: number): Promise<AvaliacaoGeral[]> {
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/${ano}
    `);
  if (!response || response.status !== 200) {
    console.error("Erro ao listar avaliações gerais por ano:", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar avaliações gerais por ano",
    );
  }
  return response.data.map((data: AvaliacaoGeralJson) => new AvaliacaoGeral(data));
}

async function listarAvaliacoesPorAvaliacaoGeral(idAvaliacaoGeral: number): Promise<Avaliacao[]> {
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/${idAvaliacaoGeral}/avaliacoes
    `);
  if (!response || response.status !== 200) {
    console.error("Erro ao listar avaliações por avaliação geral:", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar avaliações por avaliação geral",
    );
  }
  return response.data.map((data: AvaliacaoJson) => new Avaliacao(data));
}

async function listarRespostasAvaliacaoPorAvaliacaoGeral(
  avId: number,
): Promise<RetornoRespostaAvaliacao> {
  const response = await Service.$http.get(`
        ${process.env.VUE_APP_API_SERIGY_JS}/avaliacao-geral/${avId}/respostas
    `);
  if (!response || response.status !== 200) {
    console.error("Erro ao listar respostas de avaliação", avId, response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar respostas de avaliação",
    );
  }
  return new RetornoRespostaAvaliacao(response.data)
}

async function alterarHashUg(codUgSagres: string): Promise<Ug> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_SERIGY_JS}/unidade-gestora/${codUgSagres}/hash`
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao gerar hash da UG", codUgSagres, response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao gerar hash da UG",
    );
  }
  return new Ug(response.data);
}

async function gerarHashUgLogada(): Promise<Ug> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_SERIGY_JS}/unidade-gestora/hash`
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao gerar hash da UG logada", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao gerar hash da UG logada",
    );
  }
  return new Ug(response.data);
}

async function alterarUrlTansparencia(codUgSagres: string, urlTransparencia: string): Promise<Ug> {
  const response = await Service.$http.patch(
    `${process.env.VUE_APP_API_SERIGY_JS}/unidade-gestora/${codUgSagres}/urltransparencia`,
    { url: urlTransparencia },
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao alterar url da transparência", codUgSagres, response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao alterar URL da transparência da UG",
    );
  }
  return new Ug(response.data);
}

async function listarUgs(): Promise<Ug[]> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_SERIGY_JS}/unidade-gestora`
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao listar UGs", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao listar UGs",
    );
  }
  return response.data.map((data: UgJson) => new Ug(data));
}

async function getUgLogada(): Promise<Ug> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_API_SERIGY_JS}/unidade-gestora/logada`
  );
  if (!response || response.status !== 200) {
    console.error("Erro ao recuperar UG", response);
    throw new PortalError(
      response?.data?.msg ?? "Falha ao buscar UG",
    );
  }
  return new Ug(response.data);
}

export default serigyService;
